import Button from '@app-studio-web/ui/components/Button'
import PageLayout from '@app-studio-web/ui/components/PageLayout'
import { saveAnswer } from '@utils/onboardingStorage'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

type GameState = 'intro' | 'showing' | 'input' | 'wrong' | 'result'

const QUESTION_KEY = 'memory_sequence'

const ICONS = ['🍎', '🌿', '💧', '🔥', '⭐', '🌙', '💜', '🧬', '🫀']

const TOTAL_ROUNDS = 5

function getMemoryRating(score: number): {
  key: string
  emoji: string
  answerKey: string
} {
  if (score >= 5)
    return { key: 'excellent', emoji: '🧠', answerKey: 'excellent' }
  if (score >= 4) return { key: 'great', emoji: '🎯', answerKey: 'great' }
  if (score >= 3) return { key: 'average', emoji: '👍', answerKey: 'average' }
  return { key: 'needsWork', emoji: '💪', answerKey: 'needs-work' }
}

function generateSequence(length: number): number[] {
  const seq: number[] = []
  for (let i = 0; i < length; i++) {
    seq.push(Math.floor(Math.random() * ICONS.length))
  }
  return seq
}

export default function MemoryGamePage() {
  const { t } = useTranslation()
  const submit = useSubmit()

  const [gameState, setGameState] = useState<GameState>('intro')
  const [round, setRound] = useState(0)
  const [score, setScore] = useState(0)
  const [sequence, setSequence] = useState<number[]>([])
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const [playerInput, setPlayerInput] = useState<number[]>([])
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const sequenceLength = round + 3

  const showSequence = useCallback((seq: number[]) => {
    setGameState('showing')
    setHighlightIndex(-1)
    let i = 0
    const show = () => {
      if (i < seq.length) {
        setHighlightIndex(seq[i])
        timerRef.current = setTimeout(() => {
          setHighlightIndex(-1)
          timerRef.current = setTimeout(() => {
            i++
            show()
          }, 250)
        }, 600)
      } else {
        setGameState('input')
      }
    }
    timerRef.current = setTimeout(show, 500)
  }, [])

  const startRound = useCallback(
    (roundNum: number) => {
      const len = roundNum + 3
      const seq = generateSequence(len)
      setSequence(seq)
      setPlayerInput([])
      setRound(roundNum)
      showSequence(seq)
    },
    [showSequence],
  )

  const handleStart = () => {
    setScore(0)
    startRound(0)
  }

  const handleTileClick = useCallback(
    (iconIndex: number) => {
      if (gameState !== 'input') return

      const nextInput = [...playerInput, iconIndex]
      setPlayerInput(nextInput)

      const currentPos = nextInput.length - 1

      if (nextInput[currentPos] !== sequence[currentPos]) {
        setGameState('wrong')
        if (round + 1 >= TOTAL_ROUNDS) {
          timerRef.current = setTimeout(() => setGameState('result'), 1200)
        } else {
          timerRef.current = setTimeout(() => startRound(round + 1), 1200)
        }
        return
      }

      if (nextInput.length === sequence.length) {
        const newScore = score + 1
        setScore(newScore)
        if (round + 1 >= TOTAL_ROUNDS) {
          timerRef.current = setTimeout(() => setGameState('result'), 800)
        } else {
          timerRef.current = setTimeout(() => startRound(round + 1), 800)
        }
      }
    },
    [gameState, playerInput, sequence, round, score, startRound],
  )

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const rating = getMemoryRating(score)
    saveAnswer(QUESTION_KEY, {
      question_key: QUESTION_KEY,
      question: 'Memory sequence test',
      answer_key: rating.answerKey,
      answer: `${score}/${TOTAL_ROUNDS} (${rating.answerKey})`,
    })

    submit(event.currentTarget)
  }

  const rating = getMemoryRating(score)

  return (
    <PageLayout
      background={{
        gradient:
          'linear-gradient(to bottom, #c8d5b9 0%, #ddd5c7 50%, #e8d4c8 100%)',
      }}
    >
      <Form
        className="flex h-full min-h-[calc(100dvh-7rem)] flex-col"
        method="post"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          {gameState === 'intro' && (
            <>
              <div className="mb-6 text-6xl">🧠</div>
              <h1 className="custom-heading font-headline mb-4 max-w-sm text-3xl leading-tight font-normal text-balance">
                {t('pages.onboarding.memoryGame.heading')}
              </h1>
              <p className="mb-8 max-w-xs text-sm text-neutral-600">
                {t('pages.onboarding.memoryGame.description')}
              </p>
              <button
                type="button"
                onClick={handleStart}
                className="bg-primary rounded-2xl px-10 py-4 text-lg font-bold text-white hover:opacity-90 active:scale-[0.99]"
              >
                {t('pages.onboarding.memoryGame.startButton')}
              </button>
            </>
          )}

          {(gameState === 'showing' ||
            gameState === 'input' ||
            gameState === 'wrong') && (
            <>
              <p className="mb-2 text-sm font-medium text-neutral-500">
                {t('pages.onboarding.memoryGame.round', {
                  current: round + 1,
                  total: TOTAL_ROUNDS,
                })}
              </p>
              <p className="mb-1 text-xs text-neutral-400">
                {t('pages.onboarding.memoryGame.sequenceLength', {
                  count: sequenceLength,
                })}
              </p>
              <p className="mb-6 text-sm font-semibold text-neutral-700">
                {gameState === 'showing'
                  ? t('pages.onboarding.memoryGame.watch')
                  : gameState === 'wrong'
                    ? t('pages.onboarding.memoryGame.wrong')
                    : t('pages.onboarding.memoryGame.yourTurn')}
              </p>

              <div className="grid grid-cols-3 gap-3">
                {ICONS.map((icon, idx) => (
                  <button
                    type="button"
                    key={idx}
                    disabled={gameState !== 'input'}
                    onClick={() => handleTileClick(idx)}
                    className={twMerge(
                      'flex h-[72px] w-[72px] items-center justify-center rounded-xl text-3xl transition-all duration-200',
                      highlightIndex === idx
                        ? 'scale-110 bg-amber-300 shadow-lg ring-2 ring-amber-400'
                        : 'bg-white/70 shadow-md backdrop-blur',
                      gameState === 'input' &&
                        'cursor-pointer hover:bg-white active:scale-95',
                      gameState === 'wrong' && 'opacity-60',
                      gameState === 'showing' && 'pointer-events-none',
                    )}
                  >
                    {icon}
                  </button>
                ))}
              </div>

              {gameState === 'input' && (
                <div className="mt-4 flex items-center gap-1">
                  {sequence.map((_, i) => (
                    <div
                      key={i}
                      className={twMerge(
                        'h-2 w-2 rounded-full transition-colors',
                        i < playerInput.length
                          ? 'bg-neutral-700'
                          : 'bg-neutral-300',
                      )}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {gameState === 'result' && (
            <>
              <div className="mb-4 text-6xl">{rating.emoji}</div>
              <h2 className="custom-heading font-headline mb-2 text-3xl font-normal">
                {t('pages.onboarding.memoryGame.resultHeading')}
              </h2>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="font-headline text-5xl font-bold text-neutral-800">
                  {score}
                </span>
                <span className="text-lg text-neutral-500">
                  / {TOTAL_ROUNDS}
                </span>
              </div>
              <p className="mb-2 text-lg font-medium text-neutral-700">
                {t(`pages.onboarding.memoryGame.rating.${rating.key}`)}
              </p>
              <p className="max-w-xs text-sm text-neutral-500">
                {t('pages.onboarding.memoryGame.resultDescription')}
              </p>
            </>
          )}
        </div>

        {gameState === 'result' && (
          <div className="mt-8 mb-8">
            <Button
              dataTestId="button-continue"
              title={t('shared.continue')}
              type="submit"
            />
          </div>
        )}
      </Form>
    </PageLayout>
  )
}
